import { Option } from '@almbrand/selectfield';
import classNames from 'classnames';
import { Component } from 'components/Component';
import { useModal } from 'hooks';
import { useEffect, useState } from 'react';
import { useAppDispatch } from 'store/index';
import { setModal } from 'store/slices/modalSlice';
import { getComponentProperties } from '../../../services/MapFieldService';
import { SystemIconsComponent } from '../SystemIconsComponent';
import styles from './CalculationOptions.module.scss';

interface CalculationOptionsProps {
	buttons: CalculationOptionsBlock[];
	allCalculationsDone?: boolean;
	stepContentId?: string;
	setValue?: any;
	ctaSubmitStep?: () => void;
	stepTitle?: string;
	calculationOptionChanged?: () => void;
}

export const CalculationOptions: React.FC<CalculationOptionsProps> = ({
	buttons,
	allCalculationsDone,
	stepContentId,
	setValue,
	ctaSubmitStep,
	stepTitle,
	calculationOptionChanged,
	...props
}) => {
	const { displayModal } = useModal();
	const dispatch = useAppDispatch();

	// State to keep track of the selected dropdown value for each button (by index)
	const [dropdownValue, setDropdownValue] = useState<{ [key: string]: Option }>({});
	// notify parent that an option has changed
	const [notifyCalculation, setNotifyCalculation] = useState<number>(0);

	// On mount, initialize dropdownValue for each button to its default value (or first option)
	useEffect(() => {
		let field;
		buttons.forEach((button) => {
			button.modal.fields?.items?.forEach((f) => {
				field = (f as any).content;
				if (field?.contentType === 'SelectFieldBlock') {
					// If there is no chosenOption, set the default value (either the one marked as default or the first option)
					const defaultOption =
						field.chosenOption === undefined
							? { ...field.options[0] }
							: { ...field.options[field.chosenOption] };

					const name = field.uniqueIdentifier?.uid;
					if (name) {
						defaultOption.label = defaultOption.key; // Ensure the label is set for display
						setDropdownValue((prev) => ({ ...prev, [name]: defaultOption }));
						setValue(name, {
							id: name,
							value: defaultOption.value,
							label: defaultOption.label,
							type: field.contentType,
							stepTitle: stepTitle ?? 'NAME_NOT_PROVIDED',
						}); // set value on form
						// setDefaultValue(option); // set default value on select field if there is no value in form (e.g. when coming back to a step)
						ctaSubmitStep?.();
					}
				}
			});
		});
	}, []);

	const onSelectionChange = (selection) => {
		setDropdownValue((prev) => ({
			...prev,
			[selection.target.name]: { label: selection.target.value.label, value: selection.target.value.value },
		}));
		dispatch(setModal({ displayModal: false }));
		setNotifyCalculation((prev) => prev + 1); // trigger useEffect to notify parent of change
	};

	useEffect(() => {
		calculationOptionChanged?.();
	}, [notifyCalculation]);

	const mapComponents = (modal: Modal) => {
		const res = modal.fields?.items?.map((contentItem: FieldsContent, index) => {
			// const name = contentItem.content.uniqueIdentifier?.uid ?? 'NO_UNIQUE_ID_FOR_CONTENT_ITEM';

			// these formFields (if they are such) will be part of the form and therefore part of form validation!
			// This behavior we will have to look into
			const updatedComponentProps = getComponentProperties(stepContentId, contentItem, index, {
				setValue,
				ctaSubmitStep,
				onSelectionChange,
				...props,
			});
			if (contentItem?.content?.contentType === 'SelectFieldBlock') {
				// Pass the chosenOption to the SelectFieldComponent
				updatedComponentProps.chosenOption = (contentItem as any).content.chosenOption ?? 0;
			}
			return updatedComponentProps ? (
				<Component
					key={contentItem.content.contentId}
					{...updatedComponentProps}
					submitStep={ctaSubmitStep}
				/>
			) : undefined;
		});
		return res;
	};

	const getLabelText = (modal) => {
		const name = modal?.fields?.items[0]?.content?.uniqueIdentifier?.uid;
		return dropdownValue[name]?.label || '';
	};
	return (
		<div className={classNames(styles.CalculationOptions__container)}>
			{buttons.map(({ modal, icon }) => (
				<button
					className={classNames(
						styles.CalculationOptions,
						!allCalculationsDone && styles.CalculationOptions__disabled
					)}
					key={modal.subtitle}
					disabled={!allCalculationsDone}
					onClick={(e) => {
						e.preventDefault();
						displayModal(modal.title, modal.subtitle, modal.content, mapComponents(modal));
					}}
				>
					{/* Icon for the button */}
					{icon && (
						<SystemIconsComponent
							icon={icon.icon as any}
							height={icon.height}
							width={icon.width}
							themeName={'Handled-by-SystemIconsComponent' as any}
						/>
					)}
					{/* Subtitle label */}
					<span className={classNames(styles.CalculationOptions__label)}>{modal.linkText}</span>
					{/* value label, shows the selected dropdown value label */}
					<span className={classNames(styles.CalculationOptions__value)}>{getLabelText(modal)}</span>
				</button>
			))}
		</div>
	);
};
